import React, { useMemo, useState } from 'react';

import { Link as GatsbyLink, graphql, PageProps } from 'gatsby';

import { Button, Grid2 as Grid, Link, styled } from '@mui/material';

import {
  BreadCrumbData,
  BreadCrumbs,
  ResponsiveGrid,
  Line as SharedLine,
  Tag,
} from '@mewa/ui';

import { localeBaseUrl } from '../../../libs/alternative-links';
import { slugifyByLang } from '../../../libs/slugify';
import t from '../../../libs/translate';
import { NavigationData } from '../../components/header/types';
import { PageHeadlineWrapper } from '../../components/page-headline-wrapper';
import { GlobalPageContext } from '../../types';
import { FilterTabs } from '../components/filter-tabs/filter-tabs';
import { StrapiImage } from '../dynamic-content-page/content-components/image';
import { DownloadSlider } from './download-slider';
import { formatDate } from './helper';
import styles from './overview.module.scss';
import { PressContactContainer } from './press-contact';
import { MediaDownload, PressContact, PressRelease } from './types';

export { Head } from '../../layouts/head';

type PageContext = GlobalPageContext;

type Props = NavigationData & {
  allStrapiPressRelease: { totalCount: number; nodes: PressRelease[] };
  strapiPressOverview: {
    name: string;
    pressDownloads: MediaDownload[];
  };
  pressContact: PressContact;
  translations: {
    filter_tabs_all_topics: string;
    show_more_press_releases: string;
    title_press_downloads_tab: string;
    filter_tabs_sorting: string;
    filter_tabs_oldest_first: string;
    filter_tabs_newest_first: string;
  };
};

export const query = graphql`
  query ($marketRegex: String!, $lang: String!, $locale: String!) {
    ...NavigationFragment
    allStrapiPressRelease(
      filter: { locale: { eq: $locale } }
      sort: { Date: DESC }
    ) {
      totalCount
      nodes {
        date: Date
        title: Headline
        topics: Topics {
          topic: Topic
        }
        mediaDownloads: MediaDownload {
          thumbnailImage: ThumbnailImage {
            fields {
              assetUrl
            }
            width
            height
          }
          thumbnailAlternativeText: ThumbnailAlternativeText
          mediaToDownload: MediaToDownload {
            fields {
              assetUrl
            }
            extension: ext
            name
            mime
            height
            width
          }
        }
      }
    }
    strapiPressOverview(locale: { eq: $locale }) {
      name
      pressDownloads: MediaDownload {
        caption: Caption
        thumbnailImage: ThumbnailImage {
          fields {
            assetUrl
          }
          width
          height
        }
        thumbnailAlternativeText: ThumbnailAlternativeText
        mediaToDownload: MediaToDownload {
          fields {
            assetUrl
          }
          extension: ext
          name
          mime
          width
          height
        }
      }
    }
    pressContact: strapiPressContact(locale: { eq: $locale }) {
      title: Title
      mewa: PressContactAtMewa {
        data {
          childMarkdownRemark {
            html
          }
        }
      }
      communicationAgency: CommunicationAgency {
        data {
          childMarkdownRemark {
            html
          }
        }
      }
    }
    translations: translation(locale: { eq: $locale }) {
      filter_tabs_all_topics
      show_more_press_releases
      title_press_downloads_tab
      filter_tabs_sorting
      filter_tabs_oldest_first
      filter_tabs_newest_first
    }
  }
`;

export const PressOverview = ({
  data: {
    allStrapiPressRelease,
    strapiPressOverview,
    pressContact,
    translations,
    navigationLinks,
  },
  pageContext,
}: PageProps<Props, PageContext>) => {
  const SHOW_MORE_ARTICLES_COUNT = 6;

  const [filterIndex, setFilterIndex] = useState<number>(0);
  const [sortIndex, setSortIndex] = useState<number>(0);
  const [articleCount, setArticleCount] = useState<number>(
    SHOW_MORE_ARTICLES_COUNT + 1,
  );
  const pressTabTitle = t(translations, 'title_press_downloads_tab');
  const allTopicsString = t(translations, 'filter_tabs_all_topics');

  const breadCrumbData: BreadCrumbData[] = [
    {
      name: navigationLinks.navigation.company.name,
      slug: `${pageContext.langPrefix}/${navigationLinks.navigation.company.slug}/`,
    },
    {
      name: navigationLinks.pressOverview.name,
      slug: '',
    },
  ];

  const loadMoreArticles = () => {
    setArticleCount((prev) => prev + SHOW_MORE_ARTICLES_COUNT);
  };

  // Wrap in memo, so we don't have to recreate the callbacks which depend on
  // topicTabs on every render.
  const filterCriteria: string[] = useMemo(() => {
    const usedTopics = Array.from(
      new Set(
        allStrapiPressRelease.nodes.flatMap((p) =>
          p.topics.map(({ topic }) => topic),
        ),
      ),
    );
    return [allTopicsString, ...usedTopics, pressTabTitle];
  }, [allStrapiPressRelease, pressTabTitle, allTopicsString]);

  const itemsSortFunctions = [
    {
      fn: (a1: PressRelease, a2: PressRelease) =>
        Date.parse(a2.date) - Date.parse(a1.date),
    },
    {
      fn: (a1: PressRelease, a2: PressRelease) =>
        Date.parse(a1.date) - Date.parse(a2.date),
    },
  ];

  const filteredItems = (
    filterCriteria[filterIndex] === allTopicsString
      ? allStrapiPressRelease.nodes
      : allStrapiPressRelease.nodes.filter((pressRelease) =>
          pressRelease.topics
            .map(({ topic }) => topic)
            .includes(filterCriteria[filterIndex]),
        )
  ).sort(itemsSortFunctions[sortIndex].fn);

  return (
    <>
      <BreadCrumbs
        data={breadCrumbData}
        showSameDataOnMobile={true}
        localeBaseUrl={localeBaseUrl(pageContext.locale)}
        LinkComponent={GatsbyLink}
      />
      <PageHeadlineWrapper
        title={strapiPressOverview.name}
        sx={{
          marginBottom: ['24px', '40px'],
          marginTop: { md: '200px' },
        }}
      />
      <FilterTabs
        filterCriteria={filterCriteria}
        translations={translations}
        lang={pageContext.lang}
        onSortingChange={setSortIndex}
        onFilterChange={setFilterIndex}
        WrapperComponent={({ children }) => (
          <ResponsiveGrid>
            <Grid size={12}>{children}</Grid>
          </ResponsiveGrid>
        )}
      ></FilterTabs>
      <ArticlesWrapper>
        {filterCriteria[filterIndex] === pressTabTitle ? (
          <DownloadSlider downloads={strapiPressOverview.pressDownloads} />
        ) : (
          filteredItems.length > 0 && (
            <ResponsiveGrid>
              {filteredItems
                .slice(0, articleCount)
                .map((pressRelease, index) => (
                  <Grid
                    key={index}
                    size={{
                      xs: 12,
                      sm: index === 0 ? 12 : 6,
                      md: index === 0 ? 12 : 4,
                    }}
                  >
                    <Article
                      key={pressRelease.title}
                      date={pressRelease.date}
                      title={pressRelease.title}
                      mediaDownloads={pressRelease.mediaDownloads}
                      topics={pressRelease.topics}
                      href={`./${slugifyByLang(
                        pressRelease.title,
                        pageContext.lang,
                      )}`}
                      locale={pageContext.locale}
                      isFirstArticle={index === 0}
                    />
                  </Grid>
                ))}

              {articleCount < filteredItems.length && (
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                  className={styles.showMoreButton}
                >
                  <Button
                    variant="text"
                    color="secondary"
                    onClick={loadMoreArticles}
                  >
                    {t(translations, 'show_more_press_releases')}
                  </Button>
                </div>
              )}
            </ResponsiveGrid>
          )
        )}
      </ArticlesWrapper>
      <PressContactWrapper>
        <PressContactContainer
          title={pressContact.title}
          mewaContact={pressContact.mewa}
          communicationAgency={pressContact.communicationAgency}
        ></PressContactContainer>
      </PressContactWrapper>
    </>
  );
};

PressOverview.noContactTeaser = true;

export default PressOverview;

const ArticlesWrapper = styled('div')({
  marginTop: '40px',
});

export const Article = React.memo(
  ({
    date,
    title,
    topics,
    mediaDownloads,
    href,
    locale,
    isFirstArticle = false,
  }: PressRelease & {
    href: string;
    locale: string;
    isFirstArticle?: boolean;
  }) => {
    const firstImageMedia = mediaDownloads.find((m) =>
      m.mediaToDownload.mime.startsWith('image/'),
    );

    return (
      <>
        <div
          className={isFirstArticle ? styles.firstArticle : styles.tileArticle}
        >
          <GatsbyLink to={href} className={styles.imageLink}>
            <StrapiImage
              className={styles.image}
              image={firstImageMedia?.mediaToDownload}
              alt={firstImageMedia?.thumbnailAlternativeText}
            />
          </GatsbyLink>

          <div className={styles.articleContent}>
            <div>
              <p className={styles.date}>{formatDate(date, locale)}</p>
              <h2 className={styles.title}>
                <Title component={GatsbyLink} to={href} underline="none">
                  {title}
                </Title>
              </h2>
            </div>

            {topics && (
              <Tags>
                {topics.map((tag) => (
                  <StyledTag key={tag.topic} tagTheme="light">
                    {tag.topic}
                  </StyledTag>
                ))}
              </Tags>
            )}
          </div>
        </div>
        {isFirstArticle && <Line />}
      </>
    );
  },
);

const Title = styled(Link)({
  fontSize: 'inherit',
  lineHeight: 'inherit',
}) as typeof Link;

const Line = () => (
  <Grid sx={{ display: ['none', 'block'] }} size={12}>
    <SharedLine sx={{ marginTop: '98px', marginBottom: '98px' }} />
  </Grid>
);

const PressContactWrapper = styled('div')(() => ({
  marginBottom: ['96px', '126px'],
}));

const Tags = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
}));

const StyledTag = styled(Tag)({
  margin: '0 7px 15px 0',
});
